import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Table,
  Button,
  Typography,
  Descriptions,
  Alert,
  Divider,
  Space,
  Skeleton,
  Flex,
} from 'antd';
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import customerService from '../../services/customerService';
import { useCustomerRightRail, useCustomerRightRailMeta, useCustomerWorkspace } from '../../contexts/CustomerWorkspaceContext';
import WorkspaceEventsPanel, { workspaceEventSubscribeNoOp } from '../../components/workspaces/WorkspaceEventsPanel';
import PageHeader from '../../components/common/PageHeader';
import PageShell from '../../components/common/PageShell';
import TotalRow from '../../components/common/TotalRow';
import DocumentLineDescription from '../../components/common/DocumentLineDescription';
import { PaymentStatusBadge, InvoiceStatusBadge } from '../../components/common/StatusBadge';
import { formatCurrency, getCurrencyCode } from '../../utils/format';
import { isDueDateDisplayed } from '../../utils/documentDisplay';
import DocumentProgressSteps from '../../components/common/DocumentProgressSteps';
import DocumentDetailActionBar from '../../components/common/DocumentDetailActionBar';
import DocumentShareModal from '../../components/common/DocumentShareModal';

const { Title, Text, Paragraph } = Typography;

const InvoiceDetailPage = () => {
  const { publicId } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [workspaceUnread, setWorkspaceUnread] = useState(0);

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await customerService.getInvoice(publicId);
      setInvoice(data);
    } catch (err) {
      setError(err?.response?.status === 404
        ? 'Invoice not found.'
        : 'Failed to load invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [publicId]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const loadWorkspaceFeed = useCallback(
    () => customerService.getInvoiceWorkspaceFeed(publicId),
    [publicId],
  );

  const postWorkspaceEvent = useCallback(
    (formData) => customerService.postInvoiceEvent(publicId, formData),
    [publicId],
  );

  const postWorkspaceEventReply = useCallback(
    (payload) => {
      const fd = new FormData();
      fd.append('workspace_event_public_id', payload.workspaceEventPublicId);
      if (payload.body) fd.append('body', payload.body);
      (payload.files || []).forEach((f) => fd.append('files[]', f));
      return customerService.postInvoiceEventReply(publicId, fd);
    },
    [publicId],
  );

  const workspaceEventsConfig = useMemo(
    () => ({
      canUpload: true,
      canComment: true,
      showCustomerVisibilityToggle: false,
      loadFeed: loadWorkspaceFeed,
      portalHandlers: { postWorkspaceEvent, postWorkspaceEventReply },
    }),
    [loadWorkspaceFeed, postWorkspaceEvent, postWorkspaceEventReply],
  );

  const workspaceRail = useMemo(
    () =>
      !publicId || error || !invoice ? null : (
        <WorkspaceEventsPanel
          entityPublicId={publicId}
          panelLayout="rail"
          subscribeWorkspaceEvent={workspaceEventSubscribeNoOp}
          config={workspaceEventsConfig}
          onUnreadCountChange={setWorkspaceUnread}
        />
      ),
    [publicId, error, invoice, workspaceEventsConfig],
  );

  useCustomerRightRail(workspaceRail);

  const panelMeta = useMemo(
    () => ({
      title: 'Workspace',
      timelineHref: publicId ? `/workspaces/invoice/${publicId}` : null,
      unreadCount: workspaceUnread,
    }),
    [publicId, workspaceUnread],
  );
  useCustomerRightRailMeta(panelMeta);

  const { rightPanelOpen, setRightPanelOpen, setMobileRailOpen } = useCustomerWorkspace();
  const handleOpenWorkspace = useCallback(() => {
    if (window.matchMedia('(min-width: 992px)').matches) {
      setRightPanelOpen((open) => !open);
    } else {
      setMobileRailOpen(true);
    }
  }, [setRightPanelOpen, setMobileRailOpen]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await customerService.downloadInvoicePDF(publicId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${invoice?.sale_number ?? publicId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* server PDF not yet available */
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <PageShell>
        <Skeleton active paragraph={{ rows: 12 }} />
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <Alert
          type="error"
          title={error}
          showIcon
          action={<Button size="small" variant="filled" onClick={fetchInvoice}>Retry</Button>}
        />
      </PageShell>
    );
  }

  if (!invoice) return null;

  const currency = getCurrencyCode(invoice.currency);
  const isPaid = String(invoice.status_payment).toLowerCase() === 'paid';
  const showDueDate = isDueDateDisplayed(invoice.settings);
  const isOverdue = showDueDate && !isPaid && invoice.date_due && dayjs(invoice.date_due).isBefore(dayjs(), 'day');
  const lineItems = invoice.items ?? [];

  const billingCustomer = Array.isArray(invoice.customers)
    ? invoice.customers.find((c) => c.type === 'billing') ?? invoice.customers[0]
    : null;

  return (
    <PageShell>
      <PageHeader
        title={`Invoice ${invoice.sale_number ?? ''}`}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Invoices', path: '/invoices' },
          { label: invoice.sale_number ?? publicId },
        ]}
      />

      <DocumentDetailActionBar
        onBack={() => navigate('/invoices')}
        onShare={() => setShareOpen(true)}
        onDownload={handleDownload}
        downloading={downloading}
        onWorkspace={handleOpenWorkspace}
        workspaceUnread={workspaceUnread}
        workspaceActive={rightPanelOpen}
      />

      <DocumentShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        variant="invoice"
      />

      {isPaid ? (
        <Alert
          type="success"
          icon={<CheckCircleFilled />}
          showIcon
          title={<Text strong>This invoice has been paid in full.</Text>}
          style={{ marginBottom: 20, borderRadius: 6 }}
        />
      ) : isOverdue ? (
        <Alert
          type="error"
          icon={<ExclamationCircleFilled />}
          showIcon
          title={(
            <Text strong>
              This invoice is overdue.
              {invoice.amount_due > 0 && ` Amount due: ${formatCurrency(invoice.amount_due, currency)}`}
            </Text>
          )}
          style={{ marginBottom: 20, borderRadius: 6 }}
        />
      ) : invoice.amount_due > 0 ? (
        <Alert
          type="warning"
          showIcon
          title={<Text strong>Outstanding balance: {formatCurrency(invoice.amount_due, currency)}</Text>}
          style={{ marginBottom: 20, borderRadius: 6 }}
        />
      ) : null}

      <Row gutter={[16, 16]} align="top">
        <Col xs={24} lg={24} xl={7}>
          <DocumentProgressSteps
            variant="invoice"
            document={invoice}
            onDownloadPdf={handleDownload}
            downloading={downloading}
            layout="sidebar"
          />
        </Col>
        <Col xs={24} lg={24} xl={17}>
          <Card style={{ borderRadius: 8, border: '1px solid var(--varexe-border-color)', boxShadow: 'var(--varexe-box-shadow-sm)' }}>
            <Flex justify="space-between" align="flex-start" wrap="wrap" style={{ marginBottom: 24, gap: 12 }}>
              <Flex vertical style={{ gap: 6 }}>
                <Title level={3} style={{ margin: 0, fontFamily: 'Poppins, sans-serif' }}>
                  {invoice.sale_number}
                </Title>
                <Space size={4}>
                  <InvoiceStatusBadge status={invoice.status} />
                  <PaymentStatusBadge status={invoice.status_payment} />
                </Space>
              </Flex>
              <Flex vertical align="flex-end">
                <Text style={{ fontSize: 28, fontWeight: 700, fontFamily: 'Poppins, sans-serif', color: 'var(--varexe-gray-900)' }}>
                  {formatCurrency(invoice.amount_total, currency)}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>Total Amount</Text>
              </Flex>
            </Flex>

            <Divider style={{ margin: '16px 0' }} />

            <Row gutter={[24, 16]} style={{ marginBottom: 24 }}>
              {billingCustomer && (
                <Col xs={24} md={12}>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
                    Bill To
                  </Text>
                  <Text strong style={{ display: 'block', fontSize: 15 }}>
                    {billingCustomer.full_name ?? billingCustomer.business_name ?? billingCustomer.name ?? '—'}
                  </Text>
                  {billingCustomer.email && <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>{billingCustomer.email}</Text>}
                  {billingCustomer.phone && <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>{billingCustomer.phone}</Text>}
                </Col>
              )}
              <Col xs={24} md={billingCustomer ? 12 : 24}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>
                  Details
                </Text>
                <Descriptions
                  column={1}
                  size="small"
                  colon={false}
                  labelStyle={{ color: 'var(--varexe-gray-500)', fontSize: 12, width: '46%', paddingBottom: 6 }}
                  contentStyle={{ paddingBottom: 6 }}
                >
                  <Descriptions.Item label="Invoice Date">
                    {invoice.date_issued ? dayjs(invoice.date_issued).format('MMM D, YYYY') : '—'}
                  </Descriptions.Item>
                  {showDueDate && (
                    <Descriptions.Item label="Due Date">
                      <Text style={{ color: isOverdue ? 'var(--varexe-danger)' : undefined, fontWeight: isOverdue ? 600 : 400 }}>
                        {invoice.date_due ? dayjs(invoice.date_due).format('MMM D, YYYY') : '—'}
                      </Text>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Currency">{currency}</Descriptions.Item>
                  <Descriptions.Item label="Payment Status">
                    <PaymentStatusBadge status={invoice.status_payment} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Invoice Status">
                    <InvoiceStatusBadge status={invoice.status} />
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>

            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>
              Items
            </Text>
            <Table
              dataSource={lineItems}
              rowKey={(r, i) => r.id ?? i}
              pagination={false}
              scroll={{ x: 'max-content' }}
              size="small"
              style={{ marginBottom: 24 }}
              columns={[
                {
                  title: 'Description',
                  dataIndex: 'name',
                  key: 'name',
                  render: (val, r) => (
                    <DocumentLineDescription name={val} description={r.description} />
                  ),
                },
                {
                  title: 'Qty',
                  dataIndex: 'quantity',
                  key: 'qty',
                  align: 'center',
                  width: 70,
                  render: (val) => val ?? '—',
                },
                {
                  title: 'Unit Price',
                  dataIndex: 'unit_price',
                  key: 'unit_price',
                  align: 'right',
                  width: 110,
                  render: (val) => formatCurrency(val, currency),
                },
                {
                  title: 'Total',
                  dataIndex: 'total_net',
                  key: 'total_net',
                  align: 'right',
                  width: 110,
                  render: (val, r) => {
                    const total = val ?? r.total_gross ?? (r.quantity * r.unit_price);
                    return <Text strong>{formatCurrency(total, currency)}</Text>;
                  },
                },
              ]}
              locale={{ emptyText: 'No line items' }}
            />

            <Flex vertical style={{ maxWidth: 320, marginLeft: 'auto' }}>
              <TotalRow label="Subtotal" value={formatCurrency(invoice.amount_subtotal, currency)} />
              {invoice.amount_item_discount_total > 0 && (
                <TotalRow label="Discount" value={`− ${formatCurrency(invoice.amount_item_discount_total, currency)}`} color="var(--varexe-success)" />
              )}
              {invoice.amount_tax_total > 0 && (
                <TotalRow label="Tax" value={formatCurrency(invoice.amount_tax_total, currency)} />
              )}
              {invoice.shipping_amount > 0 && (
                <TotalRow label="Shipping" value={formatCurrency(invoice.shipping_amount, currency)} />
              )}
              <Flex vertical style={{ padding: '10px 0' }}>
                <TotalRow label="Total" value={formatCurrency(invoice.amount_total, currency)} bold size={15} />
              </Flex>
              {invoice.amount_due > 0 && (
                <TotalRow label="Amount Due" value={formatCurrency(invoice.amount_due, currency)} bold size={15} color="var(--varexe-danger)" />
              )}
            </Flex>

            {invoice.settings?.sale_notes && (
              <>
                <Divider />
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
                  Notes
                </Text>
                <Paragraph style={{ fontSize: 13, color: 'var(--varexe-gray-700)' }}>{invoice.settings.sale_notes}</Paragraph>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </PageShell>
  );
};

export default InvoiceDetailPage;
